import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, TextField, InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, LabelList
} from 'recharts';
import { AssigneeCell as SharedAssigneeCell, CaseIdLink as SharedCaseIdLink, absoluteHttpUrl } from '../assigneeDisplay';
import {
  getHeapRecommendations, getDbConnectionRecommendations, getThreadRecommendations,
  getRecommendationLevelTransitions
} from '../../../services/apiService';
import { ATTENTION_IDLE_BY_PRIORITY, isAttentionIdle } from './customerHealth';
import CustomerBriefingSection from './CustomerBriefingSection';
import { formatTicketAge, formatSfTableDate, tableHeaderCellSx } from '../sfTableFormatting';
import {
  DEEP_GREEN,
  MUTED,
  INK,
  NEUTRAL_SURFACE,
  L1_ACCENT,
  L1_BADGE_BG,
  L2_ACCENT,
  L2_BADGE_BG,
  INCIDENT_ACCENT,
  WARN_ORANGE
} from '../colorTokens';

/* ---- sfTableShared helpers ---- */

const L3_ACCENT = '#0d9488';
const CHART_CREATED = '#2b6cb0';
const CHART_CLOSED = '#1A2E22';
const CHART_OPEN_BACKLOG = '#c85a00';
const ACCENT_GOOD = '#2e7d32';

/** Same fit track as Overview / Top Customers boards. */
const DETAIL_INSIGHT_GRID = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))'
  },
  gap: 1.25,
  alignItems: 'stretch',
  width: '100%',
  mb: 2
};

const TILE_GRID_2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, minWidth: 0 };
const TILE_GRID_3 = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, minWidth: 0, mb: 1 };
const TILE_GRID_4 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mb: 1, minWidth: 0 };

const SF_COL_WIDTH = {
  'Case L1': { width: '4.5%' },
  'Case L2': { width: '4.5%' },
  'Case L3': { width: '4.5%' },
  'Case ServiceNow': { width: '5.5%' },
  Subject: { width: '12%' },
  'Contact Customer': { width: '6%' },
  'Product Title': { width: '4.5%' },
  Status: { width: '5%' },
  Priority: { width: '4.5%' },
  Age: { width: '3.25%' },
  'L1 Age': { width: '3.5%' },
  'L2 Age': { width: '3.5%' },
  'L3 Age': { width: '3.5%' },
  'Created Date': { width: '7%' },
  'Last Modified Date': { width: '7%' },
  'Account owner': { width: '5%' },
  'L1 Assignee': { width: '7.5%' },
  'L2 Assignee': { width: '6.5%' },
  'L3 Assignee': { width: '6.5%' },
  Version: { width: '4%' },
  Comments: { width: '3%' }
};

const SF_HEADER_LABEL = {
  'Created Date': 'Created',
  'Last Modified Date': 'Modified',
  'Account owner': 'Account',
  'Product Title': 'Product',
  'Case ServiceNow': 'ServiceNow',
  'Contact Customer': 'Contact',
  Age: 'Total age',
  'L1 Age': 'L1 age',
  'L2 Age': 'L2 age',
  'L3 Age': 'L3 age'
};

/** Plain "Nd" under 60 days; humanized "1y 2mo" beyond that so leadership views don't show raw 3-digit day counts. */
const formatTierAgeDays = (days) => {
  if (days == null || Number.isNaN(Number(days))) return '—';
  const n = Math.max(0, Math.round(Number(days)));
  if (n < 60) return `${n}d`;
  let years = Math.floor(n / 365);
  let months = Math.round((n - years * 365) / 30);
  if (months >= 12) {
    years += 1;
    months = 0;
  }
  if (years > 0) return months > 0 ? `${years}y ${months}mo` : `${years}y`;
  return `${months}mo`;
};

const sfHeaderCellSx = (col) => ({
  ...tableHeaderCellSx,
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  px: 0.75,
  py: 0.75,
  ...(SF_COL_WIDTH[col] || { width: '1%' })
});

const sfBodyCellSx = (col) => ({
  fontSize: '0.75rem',
  color: INK,
  px: 0.75,
  py: 0.5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...(SF_COL_WIDTH[col] || { width: '1%' })
});

/** Same rendering rules as OperationalExcellenceHub -- see assigneeDisplay.js. Compact sizing for this page's denser tables. */
const AssigneeCell = (props) => <SharedAssigneeCell {...props} compact />;
const CaseIdLink = (props) => <SharedCaseIdLink {...props} compact />;

/* ---- TicketFollowUpPanel ---- */

const PREVIEW_CHARS = 420;

/** Strip Proofpoint / external-sender chrome so mail body is readable end-to-end. */
const sanitizeMailBody = (text) => {
  let s = String(text || '');
  if (!s) return '';
  s = s.replace(/ZjQcmQRYFpfptBannerStart[\s\S]*?ZjQcmQRYFpfptBannerEnd/gi, '\n');
  // Truncated banner (no end marker) — drop from marker / external-sender notice onward
  s = s.replace(/ZjQcmQRYFpfptBannerStart[\s\S]*$/gi, '\n');
  s = s.replace(/(?:^|\n)This Message Is From an External Sender[\s\S]*$/gi, '\n');
  s = s.replace(/https?:\/\/[^\s<>"']*phishalarm[^\s<>"']*/gi, '');
  s = s.replace(/https?:\/\/[^\s<>"']*proofpoint\.com[^\s<>"']*/gi, '');
  s = s.replace(/\bReport Suspi\w*\.{0,3}/gi, '');
  s = s.replace(/External Email:\s*Use caution with links and attachments\.?\s*/gi, '');
  s = s.replace(/This message came from outside your organization\.?\s*/gi, '');
  return s.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
};

const longerText = (...candidates) => {
  let best = '';
  candidates.forEach((c) => {
    const s = c == null ? '' : String(c);
    if (s.length > best.length) best = s;
  });
  return best;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

const splitLinkified = (text) => {
  const raw = String(text || '');
  if (!raw) return [];
  const re = /(https?:\/\/[^\s<>"']+)/gi;
  const parts = [];
  let lastIndex = 0;
  let match = re.exec(raw);
  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: raw.slice(lastIndex, match.index) });
    }
    let url = match[0];
    let trailing = '';
    const clipped = url.match(/^(.*?)([),.;:!?\]]+)$/);
    if (clipped) {
      url = clipped[1];
      trailing = clipped[2];
    }
    if (url) parts.push({ type: 'link', value: url });
    if (trailing) parts.push({ type: 'text', value: trailing });
    lastIndex = match.index + match[0].length;
    match = re.exec(raw);
  }
  if (lastIndex < raw.length) {
    parts.push({ type: 'text', value: raw.slice(lastIndex) });
  }
  return parts.length ? parts : [{ type: 'text', value: raw }];
};

const LinkifiedText = ({ text, sx }) => {
  if (text == null || text === '') return null;
  const parts = splitLinkified(text);
  return (
    <Typography component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...sx }}>
      {parts.map((part, index) => (
        part.type === 'link' ? (
          <Box
            key={`link-${index}`}
            component="a"
            href={part.value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: L2_ACCENT,
              fontWeight: 600,
              textDecoration: 'underline',
              wordBreak: 'break-all',
              '&:hover': { color: DEEP_GREEN }
            }}
          >
            {part.value}
          </Box>
        ) : (
          <React.Fragment key={`text-${index}`}>{part.value}</React.Fragment>
        )
      ))}
    </Typography>
  );
};

const ExpandableText = ({ text, sx, previewChars = PREVIEW_CHARS }) => {
  const [expanded, setExpanded] = useState(false);
  if (text == null || text === '') return null;
  const full = String(text);
  const needsToggle = full.length > previewChars;
  const shown = !needsToggle || expanded ? full : `${full.slice(0, previewChars).trimEnd()}…`;

  return (
    <Box sx={{ overflow: 'visible', maxWidth: '100%' }}>
      <LinkifiedText text={shown} sx={{ ...sx, overflow: 'visible' }} />
      {needsToggle && (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            mt: 0.5,
            px: 0,
            minWidth: 0,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.75rem',
            color: L2_ACCENT,
            '&:hover': { bgcolor: 'transparent', color: DEEP_GREEN }
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Box>
  );
};

const eventIcon = (type) => {
  switch (type) {
    case 'customer_email':
      return EmailOutlinedIcon;
    case 'ado_create':
    case 'ado_comment':
      return AccountTreeOutlinedIcon;
    case 'status_change':
    case 'owner_change':
      return SwapHorizIcon;
    case 'owner_update':
      return ChatBubbleOutlineIcon;
    default:
      return PersonOutlineIcon;
  }
};

const DetailRow = ({ label, value, expandable = false }) => {
  if (value == null || value === '') return null;
  return (
    <>
      <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>{label}</Typography>
      {expandable ? (
        <ExpandableText
          text={value}
          sx={{ fontWeight: 600, fontSize: '0.8rem', color: INK, display: 'block' }}
        />
      ) : (
        <LinkifiedText
          text={value}
          sx={{ fontWeight: 600, fontSize: '0.8rem', color: INK, display: 'block' }}
        />
      )}
    </>
  );
};

export const TicketFollowUpPanel = ({ ticket }) => {
  if (!ticket) return null;

  const ado = ticket.linkedWorkItem;
  const timeline = ticket.timeline || [];
  const contactEmail = ticket.suppliedEmail || ticket.contactEmail || null;
  const openingMessageRaw = ticket.description ? String(ticket.description) : '';
  const openingMessage = openingMessageRaw ? sanitizeMailBody(openingMessageRaw) : null;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 2, mt: 1 }}>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(26,46,34,0.1)', borderRadius: 1.5, overflow: 'visible' }}>
        <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 0.5 }}>
          Communication Timeline
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', mb: 1.5 }}>
          {ticket.caseNumber} · {ticket.title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {timeline.length === 0 && (
            <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>No timeline events available.</Typography>
          )}
          {timeline.map((event, idx) => {
            const Icon = eventIcon(event.type);
            const isOpeningMail = event.type === 'customer_email'
              || /opening message|case description/i.test(String(event.label || ''));
            const isNotesUpdate = event.type === 'owner_update'
              || /latest update|case notes/i.test(String(event.label || ''));
            let eventBody = event.summary;
            if (isOpeningMail) {
              // Always prefer the longest source (full DESCRIPTION over any stale truncated summary)
              eventBody = sanitizeMailBody(longerText(openingMessageRaw, event.summary));
            } else if (isNotesUpdate && ticket.caseNotes) {
              const summary = String(event.summary || '');
              if (!summary || summary.endsWith('…') || ticket.caseNotes.length > summary.length) {
                eventBody = ticket.caseNotes;
              }
            }
            return (
              <Box key={event.id} sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: NEUTRAL_SURFACE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(26,46,34,0.12)',
                      zIndex: 1
                    }}
                  >
                    <Icon sx={{ fontSize: 14, color: DEEP_GREEN }} />
                  </Box>
                  {idx < timeline.length - 1 && (
                    <Box sx={{ flex: 1, width: 2, bgcolor: 'rgba(26,46,34,0.1)', my: 0.25, minHeight: 24 }} />
                  )}
                </Box>
                <Box sx={{ pb: 2, flex: 1, minWidth: 0, overflow: 'visible' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: INK }}>
                      {event.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                      {formatDateTime(event.date)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: MUTED, mt: 0.25 }}>
                    {event.actor}
                  </Typography>
                  <Box sx={{ mt: 0.5, overflow: 'visible' }}>
                    <ExpandableText
                      key={`tl-${event.id}-${String(eventBody || '').length}`}
                      text={eventBody}
                      sx={{ fontSize: '0.85rem', color: INK, display: 'block' }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(26,46,34,0.1)', borderRadius: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 1.25 }}>
            Case snapshot
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', alignItems: 'start' }}>
            <DetailRow label="Status" value={ticket.state} />
            <DetailRow label="Priority" value={ticket.priority} />
            <DetailRow label="Origin" value={ticket.origin || ticket.emailOrigin} />
            <DetailRow label="Contact" value={ticket.contactCustomer} />
            <DetailRow label="Email" value={contactEmail} />
            <DetailRow label="Owner / manager" value={ticket.manager || ticket.assignedTo || ticket.caseOwner} />
            <DetailRow label="Manager email" value={ticket.managerEmail} />
            <DetailRow label="Created" value={formatDateTime(ticket.createdDate)} />
            <DetailRow label="Last modified" value={formatDateTime(ticket.lastModifiedDate)} />
            <DetailRow label="Latest update type" value={ticket.lastUpdateType} />
            <DetailRow label="Case notes" value={ticket.caseNotes} expandable />
          </Box>
          {openingMessage && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(26,46,34,0.08)' }}>
              <Typography sx={{ color: MUTED, fontSize: '0.75rem', mb: 0.5 }}>Opening message</Typography>
              <ExpandableText
                key={`snap-open-${openingMessage.length}`}
                text={openingMessage}
                sx={{ fontSize: '0.8rem', color: INK, display: 'block' }}
              />
            </Box>
          )}
        </Paper>

        <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(26,46,34,0.1)', borderRadius: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 1.5 }}>
            Linked ADO
          </Typography>
          {!ado ? (
            <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>
              No ADO work item linked to this case.
            </Typography>
          ) : (
            <Box>
              <Chip
                size="small"
                label={`#${ado.id}`}
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: L2_BADGE_BG, color: L2_ACCENT, mb: 1 }}
              />
              <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.9rem', mb: 1 }}>
                {ado.title}
              </Typography>
              <Divider sx={{ my: 1.25 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: '0.8rem' }}>
                <Typography sx={{ color: MUTED }}>State</Typography>
                <Typography sx={{ fontWeight: 600 }}>{ado.state}</Typography>
                <Typography sx={{ color: MUTED }}>Assigned</Typography>
                <Typography sx={{ fontWeight: 600 }}>{ado.assignedTo}</Typography>
                <Typography sx={{ color: MUTED }}>Created</Typography>
                <Typography>{formatDateTime(ado.created)}</Typography>
                <Typography sx={{ color: MUTED }}>Changed</Typography>
                <Typography>{formatDateTime(ado.changed)}</Typography>
              </Box>
              {absoluteHttpUrl(ado.url) && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  href={absoluteHttpUrl(ado.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 2,
                    textTransform: 'none',
                    borderColor: 'rgba(26,46,34,0.25)',
                    color: DEEP_GREEN,
                    fontWeight: 600
                  }}
                >
                  Open in Azure DevOps
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

/* ---- CustomerTicketsTable ---- */

/** Overview columns + status + L1/L2/L3 dwell ages */
const TABLE_COLUMNS = [
  'Case L1', 'Case L2', 'Case L3', 'Case ServiceNow', 'Subject',
  'Product Title', 'Status', 'Age', 'L1 Age', 'L2 Age', 'L3 Age',
  'Created Date', 'Last Modified Date',
  'L1 Assignee', 'L2 Assignee', 'L3 Assignee', 'Version'
];

const NUMERIC_SORT_COLS = new Set([
  'Case L1', 'Case L2', 'Case L3',
  'Age', 'L1 Age', 'L2 Age', 'L3 Age',
  'Created Date', 'Last Modified Date'
]);

const isSortEmpty = (value) =>
  value == null || value === '' || (typeof value === 'number' && (Number.isNaN(value) || value < 0));

const snowLabel = (ticket) => {
  const id = String(ticket.snowId || '').trim();
  if (id) return id;
  if (ticket.linkType === 'Service') return 'RITM';
  if (ticket.linkType === 'Incident') return 'Incident';
  return '';
};

const sortValue = (ticket, col) => {
  switch (col) {
    case 'Case L1': return Number(ticket.caseNumber) || 0;
    case 'Case L2': return Number(ticket.l2Id) || ticket.l2Id || '';
    case 'Case L3': return Number((ticket.l3Ids && ticket.l3Ids[0]) || '') || '';
    case 'Case ServiceNow': return snowLabel(ticket);
    case 'Subject': return ticket.title || '';
    case 'Product Title': return ticket.productTitle || '';
    case 'Status': return ticket.state || '';
    case 'Age': return ticket.ageDays ?? -1;
    case 'L1 Age': return ticket.l1AgeDays ?? -1;
    case 'L2 Age': return ticket.l2AgeDays ?? -1;
    case 'L3 Age': return ticket.l3AgeDays ?? -1;
    case 'Created Date': return ticket.createdDate ? new Date(ticket.createdDate).getTime() : null;
    case 'Last Modified Date': return ticket.lastModifiedDate ? new Date(ticket.lastModifiedDate).getTime() : null;
    case 'L1 Assignee': return ticket.assignedTo || ticket.manager || '';
    case 'L2 Assignee': return ticket.l2AssignedTo || '';
    case 'L3 Assignee': return ticket.l3AssignedTo || '';
    case 'Version': return ticket.adoFixVersion || '';
    default: return '';
  }
};

const CustomerTicketsTable = ({ tickets, selectedTicketId, onSelectTicket, title = 'Tickets' }) => {
  const [sortBy, setSortBy] = useState('Age');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [caseIdSearch, setCaseIdSearch] = useState('');

  const filtered = useMemo(() => {
    const q = caseIdSearch.trim().toLowerCase();
    if (!q) return tickets || [];
    return (tickets || []).filter((t) => String(t.caseNumber || '').toLowerCase().includes(q));
  }, [tickets, caseIdSearch]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => {
      const av = sortValue(a, sortBy);
      const bv = sortValue(b, sortBy);
      const aEmpty = isSortEmpty(av);
      const bEmpty = isSortEmpty(bv);
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      let cmp = 0;
      if (NUMERIC_SORT_COLS.has(sortBy) && typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [filtered, sortBy, sortDir]);

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, px: 2, pt: 2, pb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, color: DEEP_GREEN, fontSize: '1rem' }}>{title}</Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>
          {filtered.length === (tickets || []).length
            ? `${(tickets || []).length} cases`
            : `${filtered.length} of ${(tickets || []).length} cases`}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, pb: 2 }}>
        <TextField
          size="small"
          label="Case #"
          placeholder="Search case ID"
          value={caseIdSearch}
          onChange={(e) => { setCaseIdSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
              </InputAdornment>
            )
          }}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' }
          }}
        />
      </Box>

      <TableContainer sx={{ maxHeight: '70vh', overflowX: 'auto', width: '100%' }}>
        <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {TABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col}
                  sortDirection={sortBy === col ? sortDir : false}
                  title={col}
                  sx={sfHeaderCellSx(col)}
                >
                  <TableSortLabel
                    active={sortBy === col}
                    direction={sortBy === col ? sortDir : 'asc'}
                    onClick={() => handleSort(col)}
                    hideSortIcon={false}
                    sx={{
                      color: 'inherit',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: '0.75rem',
                      '&:hover': { color: DEEP_GREEN },
                      '&.Mui-active': { color: 'inherit' },
                      '& .MuiTableSortLabel-icon': {
                        color: 'inherit !important',
                        opacity: sortBy === col ? 1 : 0.45,
                        fontSize: '0.9rem'
                      }
                    }}
                  >
                    {SF_HEADER_LABEL[col] || col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={TABLE_COLUMNS.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No cases match the current search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {paged.map((ticket, index) => {
              const selected = ticket.id === selectedTicketId;
              const l3Id = (ticket.l3Ids && ticket.l3Ids[0]) || '';
              const snowId = snowLabel(ticket);
              return (
                <TableRow
                  key={ticket.id}
                  hover
                  selected={selected}
                  onClick={() => onSelectTicket(selected ? null : ticket)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: index % 2 ? 'rgba(26,46,34,0.02)' : '#fff',
                    '&.Mui-selected': { bgcolor: 'rgba(26,46,34,0.06)' }
                  }}
                >
                  <TableCell sx={sfBodyCellSx('Case L1')}>
                    <CaseIdLink
                      value={ticket.caseNumber}
                      onClick={() => onSelectTicket(selected ? null : ticket)}
                      color={L1_ACCENT}
                    />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Case L2')}>
                    <CaseIdLink value={ticket.l2Id} href={ticket.l2Url} color={L2_ACCENT} />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Case L3')}>
                    <CaseIdLink value={l3Id} href={ticket.l3Url} color={L3_ACCENT} />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Case ServiceNow')}>
                    <CaseIdLink value={snowId} href={ticket.snowUrl} color={INCIDENT_ACCENT} />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Subject')} title={ticket.title || ''}>
                    {ticket.title || '—'}
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Product Title')} title={ticket.productTitle || ''}>
                    {ticket.productTitle || '—'}
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Status')}>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: ticket.state === 'New'
                          ? '#2e7d32'
                          : ticket.state === 'WIP' || /work\s*in\s*progress/i.test(ticket.state || '')
                            ? L2_ACCENT
                            : INK
                      }}
                    >
                      {ticket.state || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ ...sfBodyCellSx('Age'), fontWeight: 700, color: '#c62828' }}
                    title={ticket.createdDate ? `Total case age · SF created ${new Date(ticket.createdDate).toLocaleString()} → now` : ''}
                  >
                    {formatTicketAge(ticket.createdDate)}
                  </TableCell>
                  <TableCell
                    sx={{ ...sfBodyCellSx('L1 Age'), fontWeight: 600, color: L1_ACCENT }}
                    title={ticket.l2MovedAt
                      ? `L1 dwell · SF created → L2 ${new Date(ticket.l2MovedAt).toLocaleString()}`
                      : (ticket.l2Id
                        ? 'L1 dwell · full case age until ADO L2 created date is available to split L1 vs L2'
                        : 'L1 dwell · still at L1 (SF created → now)')}
                  >
                    {formatTierAgeDays(ticket.l1AgeDays)}
                  </TableCell>
                  <TableCell
                    sx={{ ...sfBodyCellSx('L2 Age'), fontWeight: 600, color: L2_ACCENT }}
                    title={ticket.l2MovedAt
                      ? (ticket.l3MovedAt
                        ? `L2 dwell · L2 ${new Date(ticket.l2MovedAt).toLocaleString()} → L3 ${new Date(ticket.l3MovedAt).toLocaleString()}${ticket.l2AgeApprox ? ' (approx)' : ''}`
                        : `L2 dwell · L2 move → now${ticket.l2AgeApprox ? ' (approx)' : ''}`)
                      : (ticket.l2Id
                        ? `L2 ${ticket.l2Id} linked — need ADO created date in local data for L2-specific age`
                        : 'No L2 yet')}
                  >
                    {formatTierAgeDays(ticket.l2AgeDays)}
                  </TableCell>
                  <TableCell
                    sx={{ ...sfBodyCellSx('L3 Age'), fontWeight: 600, color: L3_ACCENT }}
                    title={ticket.l3MovedAt
                      ? `L3 dwell since ${new Date(ticket.l3MovedAt).toLocaleString()}${ticket.l3AgeApprox ? ' (approx)' : ''}`
                      : (ticket.l3Ids && ticket.l3Ids.length
                        ? 'L3 linked — need ADO created date for L3-specific age'
                        : 'No L3 yet')}
                  >
                    {formatTierAgeDays(ticket.l3AgeDays)}
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Created Date')} title={ticket.createdDate ? new Date(ticket.createdDate).toLocaleString() : ''}>
                    {formatSfTableDate(ticket.createdDate)}
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Last Modified Date')} title={ticket.lastModifiedDate ? new Date(ticket.lastModifiedDate).toLocaleString() : ''}>
                    {formatSfTableDate(ticket.lastModifiedDate)}
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('L1 Assignee')}>
                    <AssigneeCell assignedTo={ticket.assignedTo || ticket.manager} color={L1_ACCENT} bg={L1_BADGE_BG} />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('L2 Assignee')}>
                    <AssigneeCell assignedTo={ticket.l2AssignedTo} color={L2_ACCENT} bg={L2_BADGE_BG} />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('L3 Assignee')}>
                    <AssigneeCell assignedTo={ticket.l3AssignedTo} color={L3_ACCENT} bg="#e6f7f4" />
                  </TableCell>
                  <TableCell sx={sfBodyCellSx('Version')}>
                    <Typography sx={{ fontSize: '0.75rem', color: ticket.adoFixVersion ? INK : MUTED }}>
                      {ticket.adoFixVersion || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100, 500]}
      />
    </Paper>
  );
};

/* ---- CustomerDetailView ---- */

const buildVolumeTakeaway = (monthlyRows) => {
  if (!monthlyRows.length) return '';
  const last3 = monthlyRows.slice(-3);
  const opened3 = last3.reduce((s, r) => s + (Number(r.opened) || 0), 0);
  const closed3 = last3.reduce((s, r) => s + (Number(r.closed) || 0), 0);
  const net3 = opened3 - closed3;
  const latest = monthlyRows[monthlyRows.length - 1] || {};
  const openNow = Number(latest.open) || 0;
  if (net3 > 0) {
    return `Backlog grew by ${net3} over the last 3 months (${opened3} opened vs ${closed3} closed) · ${openNow} open now.`;
  }
  if (net3 < 0) {
    return `Backlog shrank by ${Math.abs(net3)} over the last 3 months (${closed3} closed vs ${opened3} opened) · ${openNow} open now.`;
  }
  return `Backlog held steady over the last 3 months (${opened3} opened, ${closed3} closed) · ${openNow} open now.`;
};

const formatShortDate = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
};
const TONE = {
  good: { color: '#2e7d32', bg: '#e8f5e9' },
  watch: { color: '#c85a00', bg: '#fbe9dd' },
  bad: { color: '#c62828', bg: '#fdecea' },
  neutral: { color: INK, bg: '#f7f7f7' }
};

const StatTile = ({ label, value, tone = 'neutral', detail = null, detailTitle = null, onDetailClick = null }) => {
  const style = TONE[tone] || TONE.neutral;
  const multiLine = typeof detail === 'string' && detail.includes('\n');
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.7,
        borderRadius: 1,
        bgcolor: style.bg,
        textAlign: 'center',
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          color: MUTED,
          letterSpacing: '0.02em',
          lineHeight: 1.25,
          wordBreak: 'break-word',
          whiteSpace: 'normal'
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: style.color, lineHeight: 1.2, mt: 0.2 }}>
        {value}
      </Typography>
      {detail ? (
        <Typography
          component={onDetailClick ? 'button' : 'span'}
          onClick={onDetailClick || undefined}
          title={detailTitle || (typeof detail === 'string' ? detail : undefined)}
          sx={{
            display: 'block',
            mt: 0.35,
            fontSize: multiLine ? '0.58rem' : '0.65rem',
            fontWeight: 600,
            color: style.color,
            lineHeight: 1.25,
            whiteSpace: multiLine ? 'pre-line' : 'nowrap',
            overflow: 'hidden',
            textOverflow: multiLine ? 'clip' : 'ellipsis',
            textAlign: multiLine ? 'left' : 'center',
            border: 'none',
            background: 'none',
            padding: 0,
            cursor: onDetailClick ? 'pointer' : 'default',
            textDecoration: onDetailClick ? 'underline' : 'none',
            width: '100%'
          }}
        >
          {detail}
        </Typography>
      ) : null}
    </Box>
  );
};

const daysSince = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000)));
};

/**
 * Same rule as isNewOpenWipStatus in server/services/topCustomersService.js --
 * kept in sync by hand, can't share one import across the server/client boundary.
 */
const queueBucket = (state) => {
  const s = String(state || '').trim();
  if (/^new$/i.test(s)) return 'New';
  if (/^open$/i.test(s)) return 'Open';
  if (/^wip$/i.test(s) || /work\s*in\s*progress/i.test(s)) return 'WIP';
  return null;
};

/** Table / queue: only New, Open, WIP (excludes Fix Applied and other active statuses). */
const isNewOpenWip = (ticket) => Boolean(queueBucket(ticket?.state));


const CaseRef = ({ caseNumber, meta, onSelect }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0, py: 0.15 }}>
    <Typography
      component="button"
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect?.(caseNumber);
      }}
      sx={{
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: onSelect ? 'pointer' : 'default',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: L2_ACCENT,
        textDecoration: onSelect ? 'underline' : 'none',
        flexShrink: 0,
        '&:hover': onSelect ? { color: DEEP_GREEN } : undefined
      }}
    >
      {caseNumber}
    </Typography>
    <Typography sx={{ fontSize: '0.65rem', color: MUTED, minWidth: 0, wordBreak: 'break-word', whiteSpace: 'normal' }}>
      {meta}
    </Typography>
  </Box>
);

const ViewCasesToggle = ({ open, onToggle }) => (
  <Typography
    component="button"
    type="button"
    onClick={onToggle}
    sx={{
      border: 'none',
      background: 'none',
      padding: 0,
      cursor: 'pointer',
      fontSize: '0.62rem',
      fontWeight: 700,
      color: L2_ACCENT,
      textDecoration: 'underline',
      flexShrink: 0,
      ml: 'auto',
      textAlign: 'right',
      '&:hover': { color: DEEP_GREEN }
    }}
  >
    {open ? 'Hide' : 'See all'}
  </Typography>
);

/** Shared header row: label left, See all always on the right edge */
const CaseGroupHeader = ({ title, showToggle, open, onToggle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, width: '100%', mb: 0.35 }}>
    <Typography
      sx={{
        fontSize: '0.62rem',
        fontWeight: 700,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {title}
    </Typography>
    {showToggle ? <ViewCasesToggle open={open} onToggle={onToggle} /> : null}
  </Box>
);

const QueueAttentionCard = ({
  newCases,
  openCases,
  wipCases,
  attentionCases,
  lastResponse,
  onSelectCase
}) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (label) => setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));

  const renderGroup = (label, rows, metaFn) => {
    if (!rows.length) return null;
    const isOpen = Boolean(expanded[label]);
    return (
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <CaseGroupHeader
          title={`${label} (${rows.length})`}
          showToggle
          open={isOpen}
          onToggle={() => toggle(label)}
        />
        {isOpen && (
          <>
            {rows.slice(0, 8).map((c) => (
              <CaseRef
                key={`${label}-${c.caseNumber}`}
                caseNumber={c.caseNumber}
                meta={metaFn(c)}
                onSelect={onSelectCase}
              />
            ))}
            {rows.length > 8 && (
              <Typography sx={{ fontSize: '0.62rem', color: MUTED, mt: 0.2 }}>+{rows.length - 8} more</Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {lastResponse && lastResponse.staleDays != null && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 1, minWidth: 0 }}>
          <StatTile
            label="Last customer response"
            value={`${formatTierAgeDays(lastResponse.staleDays)} ago`}
            tone={lastResponse.staleDays > 30 ? 'bad' : lastResponse.staleDays > 14 ? 'watch' : 'good'}
            detail={lastResponse.caseNumber ? `case ${lastResponse.caseNumber} →` : null}
            detailTitle="Jump to this case"
            onDetailClick={lastResponse.caseNumber ? () => onSelectCase?.(lastResponse.caseNumber) : null}
          />
        </Box>
      )}
      <Box sx={{ ...TILE_GRID_4 }}>
        <StatTile label="New" value={newCases.length} tone={newCases.length ? 'watch' : 'good'} />
        <StatTile label="Open" value={openCases.length} tone={openCases.length ? 'watch' : 'good'} />
        <StatTile label="WIP" value={wipCases.length} tone={wipCases.length ? 'neutral' : 'good'} />
        <StatTile
          label="Attention"
          value={attentionCases.length}
          tone={attentionCases.length ? 'bad' : 'good'}
        />
      </Box>
      <Box
        sx={{
          pt: 0.85,
          borderTop: '1px solid rgba(26,46,34,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75
        }}
      >
        {renderGroup('New', newCases, (c) => `${formatTierAgeDays(c.ageDays)} · mod ${formatShortDate(c.lastModifiedDate)}`)}
        {renderGroup('Open', openCases, (c) => `${formatTierAgeDays(c.ageDays)} · mod ${formatShortDate(c.lastModifiedDate)}`)}
        {renderGroup('WIP', wipCases, (c) => `${formatTierAgeDays(c.ageDays)} · mod ${formatShortDate(c.lastModifiedDate)}`)}
        {renderGroup(
          'Attention (idle by priority)',
          attentionCases,
          (c) => `idle ${formatTierAgeDays(c.idleDays)} · ${c.priority || '—'} · age ${formatTierAgeDays(c.ageDays)}`
        )}
      </Box>
    </Box>
  );
};

const NetMonthCard = ({ netValue, label, opened, closed, onSelectCase }) => {
  const tone = netValue > 0 ? 'bad' : netValue < 0 ? 'good' : 'neutral';
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderList = (key, title, rows, metaFn) => (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <CaseGroupHeader
        title={title}
        showToggle={rows.length > 0}
        open={Boolean(expanded[key])}
        onToggle={() => toggle(key)}
      />
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>None</Typography>
      ) : expanded[key] && (
        <>
          {rows.slice(0, 8).map((c) => (
            <CaseRef
              key={`nm-${key}-${c.caseNumber}-${c.closedDate || ''}`}
              caseNumber={c.caseNumber}
              meta={metaFn(c)}
              onSelect={onSelectCase}
            />
          ))}
          {rows.length > 8 && (
            <Typography sx={{ fontSize: '0.62rem', color: MUTED, mt: 0.2 }}>+{rows.length - 8} more</Typography>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.75, mb: 0.85 }}>
        <StatTile label="Net month" value={netValue} tone={tone} />
      </Box>
      <Box
        sx={{
          pt: 0.85,
          borderTop: '1px solid rgba(26,46,34,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75
        }}
      >
        {renderList(
          'opened',
          `Opened (${opened.length})${label ? ` · ${label}` : ''}`,
          opened,
          (c) => `created ${formatShortDate(c.createdDate)}`
        )}
        {renderList(
          'closed',
          `Closed (${closed.length})`,
          closed,
          (c) => `closed ${formatShortDate(c.closedDate)} · created ${formatShortDate(c.createdDate)}`
        )}
      </Box>
    </Box>
  );
};

const DeliveryHealthCard = ({ overdueOpen, staleOpen, oldOpen, oldestOpenDays }) => (
  <Box sx={TILE_GRID_2}>
    <StatTile label="Overdue >14d" value={overdueOpen} tone={overdueOpen ? 'watch' : 'good'} />
    <StatTile label="Stale >30d" value={staleOpen} tone={staleOpen ? 'watch' : 'good'} />
    <StatTile label="Old >60d" value={oldOpen} tone={oldOpen ? 'bad' : 'good'} />
    <StatTile label="Oldest open" value={formatTierAgeDays(oldestOpenDays)} tone="neutral" />
  </Box>
);

const StallAnalysisCard = ({ stall, onSelectCase }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (label) => setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));

  if (!stall) {
    return <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>No stall data yet.</Typography>;
  }
  const stuckTotal = (stall.stuckL1?.count || 0) + (stall.stuckL2?.count || 0) + (stall.stuckL3?.count || 0);
  const tone = stuckTotal > 0 ? 'watch' : 'good';
  const renderStuck = (label, bucket, ageKey) => {
    const rows = bucket?.cases || [];
    if (!bucket?.count) return null;
    const isOpen = Boolean(expanded[label]);
    return (
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <CaseGroupHeader
          title={`Stuck ${label} >${bucket.thresholdDays}d (${bucket.count})`}
          showToggle
          open={isOpen}
          onToggle={() => toggle(label)}
        />
        {isOpen && (
          <>
            {rows.slice(0, 6).map((c) => (
              <CaseRef
                key={`stall-${label}-${c.caseNumber}`}
                caseNumber={c.caseNumber}
                meta={`${formatTierAgeDays(c[ageKey] ?? c.ageDays)} at ${label}`}
                onSelect={onSelectCase}
              />
            ))}
            {bucket.count > 6 && (
              <Typography sx={{ fontSize: '0.62rem', color: MUTED, mt: 0.2 }}>+{bucket.count - 6} more</Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Box sx={TILE_GRID_3}>
        <StatTile label="Stuck L1" value={stall.stuckL1?.count || 0} tone={(stall.stuckL1?.count || 0) ? 'bad' : 'good'} />
        <StatTile label="Stuck L2" value={stall.stuckL2?.count || 0} tone={(stall.stuckL2?.count || 0) ? 'watch' : 'good'} />
        <StatTile label="Stuck L3" value={stall.stuckL3?.count || 0} tone={(stall.stuckL3?.count || 0) ? 'watch' : 'good'} />
      </Box>
      <Box sx={{ ...TILE_GRID_2, mb: 1 }}>
        <StatTile
          label="Median L1→L2"
          value={formatTierAgeDays(stall.medianL1toL2Days)}
          tone="neutral"
          detail={stall.samplesL1toL2 ? `${stall.samplesL1toL2} moves` : null}
        />
        <StatTile
          label="Median L2→L3"
          value={formatTierAgeDays(stall.medianL2toL3Days)}
          tone="neutral"
          detail={stall.samplesL2toL3 ? `${stall.samplesL2toL3} moves` : null}
        />
      </Box>
      {stall.bottleneck && (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: tone === 'watch' ? WARN_ORANGE : ACCENT_GOOD, mb: 0.75 }}>
          Bottleneck: {stall.bottleneck.tier} ({stall.bottleneck.stuck} stuck &gt;{stall.bottleneck.thresholdDays}d)
        </Typography>
      )}
      <Box
        sx={{
          pt: 0.85,
          borderTop: '1px solid rgba(26,46,34,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75
        }}
      >
        {renderStuck('L1', stall.stuckL1, 'l1AgeDays')}
        {renderStuck('L2', stall.stuckL2, 'l2AgeDays')}
        {renderStuck('L3', stall.stuckL3, 'l3AgeDays')}
        {(stall.recentTransitions || []).length > 0 && (
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <CaseGroupHeader
              title={`Recent transitions (${stall.recentTransitions.length})`}
              showToggle
              open={Boolean(expanded.transitions)}
              onToggle={() => toggle('transitions')}
            />
            {expanded.transitions && stall.recentTransitions.slice(0, 8).map((tr) => (
              <CaseRef
                key={`tr-${tr.caseNumber}-${tr.from}-${tr.to}-${tr.at}`}
                caseNumber={tr.caseNumber}
                meta={`${tr.from}→${tr.to} ${formatShortDate(tr.at)}${tr.daysInFrom != null ? ` · ${formatTierAgeDays(tr.daysInFrom)}` : ''}`}
                onSelect={onSelectCase}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const InsightCard = ({ title, purpose, tone = 'good', subtitle, children, centerContent = false }) => {
  const accent = tone === 'bad'
    ? TONE.bad.color
    : tone === 'watch'
      ? TONE.watch.color
      : ACCENT_GOOD;
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: '#fff',
        border: '1px solid rgba(26,46,34,0.1)',
        borderLeft: `3px solid ${accent}`,
        minWidth: 0,
        minHeight: 0,
        height: '100%',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 0,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: accent, letterSpacing: '0.03em', flexShrink: 0, wordBreak: 'break-word' }}>
        {title}
      </Typography>
      {purpose && (
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: DEEP_GREEN, mt: 0.35, lineHeight: 1.3, flexShrink: 0, wordBreak: 'break-word' }}>
          {purpose}
        </Typography>
      )}
      {subtitle && (
        <Typography sx={{ fontSize: '0.65rem', color: MUTED, mt: 0.2, flexShrink: 0, wordBreak: 'break-word' }}>
          {subtitle}
        </Typography>
      )}
      <Box
        sx={{
          mt: 0.75,
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: centerContent ? 'flex' : undefined,
          flexDirection: centerContent ? 'column' : undefined,
          justifyContent: centerContent ? 'center' : undefined
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

/**
 * Matches a Top Customer's admin-entered "Instance Name" value against
 * the combined heap/db/thread production-analysis entries (each keyed by
 * stableKey = "instanceName-instanceDomain"). Exact case-insensitive match
 * only on instanceName/friendlyName -- this field identifies one specific
 * instance (unlike Production Analysis's free-text Client Health Search,
 * which intentionally supports fuzzy multi-match + a picker), so a substring
 * fallback here would risk silently attributing another instance's health
 * data to the wrong customer. Returns the matched stableKey, or null.
 */
const matchCollaborateStableKey = (heapEntries, dbEntries, threadEntries, needle) => {
  const target = String(needle || '').trim().toLowerCase();
  if (!target) return null;
  const keys = new Set([
    ...Object.keys(heapEntries || {}),
    ...Object.keys(dbEntries || {}),
    ...Object.keys(threadEntries || {})
  ]);
  for (const key of keys) {
    const entry = heapEntries?.[key] || dbEntries?.[key] || threadEntries?.[key];
    if (!entry) continue;
    const instanceName = String(entry.instanceName || '').trim().toLowerCase();
    const friendlyName = String(entry.friendlyName || '').trim().toLowerCase();
    if (instanceName === target || friendlyName === target) return key;
  }
  return null;
};

const HEALTH_LEVEL_MUI_COLOR = { critical: 'error', warning: 'warning', good: 'success' };
const HEALTH_LEVEL_BORDER = { critical: '#d32f2f', warning: '#e08e0b', good: '#2e7d32' };

/** kind-specific bar color, matching Production Analysis's Memory/DB/Threads charts. */
const COLLAB_BAR_COLOR = {
  memory: '#1976d2',
  db: '#2e7d32',
  threads: '#e65100'
};

/** { current, proposed, unit } for the kind-specific capacity chips (GiB for memory, raw count for db/threads). */
const collabCapacity = (entry, kind) => {
  if (kind === 'memory') return { current: entry?.currentMemoryGiB, proposed: entry?.proposedMemoryGiB, unit: ' GiB', decimals: 1 };
  if (kind === 'db') return { current: entry?.currentMaxDb, proposed: entry?.proposedMaxDb, unit: '', decimals: 0, maxLabel: 'Max DB' };
  return { current: entry?.currentMaxThreads, proposed: entry?.proposedMaxThreads, unit: '', decimals: 0, maxLabel: 'Max Threads' };
};

const CollabHealthCard = ({ label, entry, kind }) => {
  const level = entry?.recommendation?.level;
  const borderColor = level ? (HEALTH_LEVEL_BORDER[level] || DEEP_GREEN) : 'rgba(26,46,34,0.15)';
  const { current, proposed, unit, decimals, maxLabel } = collabCapacity(entry, kind);
  const hasProposed = current != null && proposed != null && Math.abs(proposed - current) > 0.01;
  const chartData = (entry?.dailyViolationTotals || []).map((d) => ({
    date: d.date?.slice(5) ?? d.date,
    value: d.totalViolations ?? 0
  }));

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: '10px', borderColor: 'rgba(26,46,34,0.12)', borderLeft: `4px solid ${borderColor}`, bgcolor: '#fff' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: INK }}>{label}</Typography>
        <Chip
          size="small"
          label={level ? level.charAt(0).toUpperCase() + level.slice(1) : 'No data'}
          color={level ? (HEALTH_LEVEL_MUI_COLOR[level] || 'default') : 'default'}
          variant={level ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700, fontSize: '0.68rem' }}
        />
      </Box>

      {entry ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
            {current != null && (
              <Chip size="small" variant="outlined" label={kind === 'memory' ? `${current.toFixed(decimals)}${unit}` : `${maxLabel}: ${current}`} />
            )}
            {hasProposed && (
              <>
                {kind === 'memory' && <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>→</Typography>}
                <Chip
                  size="small"
                  color={proposed > current ? 'warning' : 'success'}
                  label={`${proposed > current ? '↑' : '↓'} ${proposed.toFixed(decimals)}${unit}`}
                />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2.5, mb: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '0.62rem', color: MUTED, textTransform: 'uppercase', fontWeight: 700 }}>Total</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: INK }}>{entry.summary?.violationEventsTotal ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.62rem', color: MUTED, textTransform: 'uppercase', fontWeight: 700 }}>Critical days</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: (entry.summary?.criticalDaysTotal || 0) > 0 ? '#d32f2f' : INK }}>
                {entry.summary?.criticalDaysTotal ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.62rem', color: MUTED, textTransform: 'uppercase', fontWeight: 700 }}>Warn days</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: INK }}>{entry.summary?.warningDaysTotal ?? '—'}</Typography>
            </Box>
          </Box>

          {entry.recommendation?.title && (
            <Typography sx={{ fontSize: '0.72rem', color: MUTED, mb: 1 }}>{entry.recommendation.title}</Typography>
          )}

          {!entry.recommendation?.title && (
            <Typography sx={{ fontSize: '0.72rem', color: MUTED, mb: 1 }}>No sustained threshold breaches</Typography>
          )}

          {chartData.length > 0 && (
            <Box sx={{ height: 150, mt: 0.5 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 14, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: MUTED }} />
                  <YAxis tick={{ fontSize: 9, fill: MUTED }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', fontSize: 11 }} />
                  <Bar dataKey="value" fill={COLLAB_BAR_COLOR[kind]} radius={[2, 2, 0, 0]} maxBarSize={22}>
                    <LabelList dataKey="value" position="top" style={{ fontSize: 9, fill: MUTED }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>No data for this client.</Typography>
      )}
    </Paper>
  );
};

const ChartCard = ({ title, purpose, subtitle, children, compact }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: '10px',
      p: compact ? '8px 10px' : '14px 16px',
      borderColor: 'rgba(26,46,34,0.12)',
      borderLeft: `3px solid ${DEEP_GREEN}`,
      bgcolor: '#fff',
      minWidth: 0,
      minHeight: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 1,
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}
  >
    <Box sx={{ mb: compact ? 0.5 : 1.25, minWidth: 0, flexShrink: 0 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: DEEP_GREEN }}>
        {title}
      </Typography>
      {purpose && (
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: DEEP_GREEN, mt: 0.35, lineHeight: 1.3 }}>
          {purpose}
        </Typography>
      )}
      {subtitle && (
        <Typography sx={{ color: MUTED, fontSize: '0.65rem', mt: 0.15, lineHeight: 1.3 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children}</Box>
  </Paper>
);

const CustomerDetailView = ({ customer, onBack, agesRefreshing = false, OpenCasesTable = null }) => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const detailRef = useRef(null);
  const insights = useMemo(() => customer.insights || {}, [customer.insights]);

  /** Memory/DB connection/thread production-analysis health for this customer's Instance Name. */
  const [collabHealth, setCollabHealth] = useState({
    loading: false, error: null, stableKey: null, heapEntry: null, dbEntry: null, threadEntry: null
  });

  useEffect(() => {
    const service = String(customer.instanceName || '').trim();
    if (!service) {
      setCollabHealth({ loading: false, error: null, stableKey: null, heapEntry: null, dbEntry: null, threadEntry: null });
      return undefined;
    }
    let cancelled = false;
    setCollabHealth((prev) => ({ ...prev, loading: true, error: null }));
    (async () => {
      try {
        const [heap, db, thread] = await Promise.all([
          getHeapRecommendations(),
          getDbConnectionRecommendations(),
          getThreadRecommendations()
        ]);
        if (cancelled) return;
        const heapEntries = heap?.entries || {};
        const dbEntries = db?.entries || {};
        const threadEntries = thread?.entries || {};
        const stableKey = matchCollaborateStableKey(heapEntries, dbEntries, threadEntries, service);
        setCollabHealth({
          loading: false,
          error: null,
          stableKey,
          heapEntry: stableKey ? heapEntries[stableKey] || null : null,
          dbEntry: stableKey ? dbEntries[stableKey] || null : null,
          threadEntry: stableKey ? threadEntries[stableKey] || null : null
        });
      } catch (err) {
        if (cancelled) return;
        setCollabHealth({
          loading: false,
          error: err.message || 'Failed to load system health data',
          stableKey: null, heapEntry: null, dbEntry: null, threadEntry: null
        });
      }
    })();
    return () => { cancelled = true; };
  }, [customer.instanceName]);

  /** Status Change History (last 30 days) for the matched instance. */
  const [collabTransitions, setCollabTransitions] = useState({ loading: false, items: [] });

  useEffect(() => {
    const stableKey = collabHealth.stableKey;
    if (!stableKey) {
      setCollabTransitions({ loading: false, items: [] });
      return undefined;
    }
    let cancelled = false;
    setCollabTransitions((prev) => ({ ...prev, loading: true }));
    (async () => {
      try {
        const [mem, thr, db] = await Promise.all([
          getRecommendationLevelTransitions('memory', 30),
          getRecommendationLevelTransitions('threads', 30),
          getRecommendationLevelTransitions('db', 30)
        ]);
        if (cancelled) return;
        const items = [
          ...(mem?.transitions || []).filter((t) => t.stableKey === stableKey).map((t) => ({ ...t, analysisLabel: 'Memory' })),
          ...(thr?.transitions || []).filter((t) => t.stableKey === stableKey).map((t) => ({ ...t, analysisLabel: 'Threads' })),
          ...(db?.transitions || []).filter((t) => t.stableKey === stableKey).map((t) => ({ ...t, analysisLabel: 'DB Connection' }))
        ].sort((a, b) => (b.dayKey || '').localeCompare(a.dayKey || ''));
        setCollabTransitions({ loading: false, items });
      } catch {
        if (!cancelled) setCollabTransitions({ loading: false, items: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [collabHealth.stableKey]);

  /** Overall summary line above the System Health cards (worst level, name, region, last analysed). */
  const collabOverview = useMemo(() => {
    const { heapEntry, dbEntry, threadEntry } = collabHealth;
    const anyEntry = heapEntry || dbEntry || threadEntry;
    if (!anyEntry) return null;
    const levels = [heapEntry?.recommendation?.level, dbEntry?.recommendation?.level, threadEntry?.recommendation?.level].filter(Boolean);
    const overallLevel = levels.includes('critical') ? 'critical' : levels.includes('warning') ? 'warning' : levels.length ? 'good' : null;
    const computedAt = [heapEntry?.computedAt, dbEntry?.computedAt, threadEntry?.computedAt].filter(Boolean).sort().pop() || null;
    return {
      displayName: anyEntry.friendlyName || anyEntry.instanceName || customer.instanceName,
      region: anyEntry.region || '',
      computedAt,
      overallLevel
    };
  }, [collabHealth, customer.instanceName]);

  const metricTones = useMemo(() => {
    const netBacklog = insights.leadershipView?.netBacklogLastMonth ?? 0;
    return {
      netMonthCard: netBacklog > 0 ? 'watch' : 'good'
    };
  }, [insights]);

  const monthlyData = useMemo(() => customer.trends?.monthly || [], [customer.trends]);
  const volumeTakeaway = useMemo(() => buildVolumeTakeaway(monthlyData), [monthlyData]);
  const periodData = useMemo(() => customer.trends?.periodLines || [], [customer.trends]);

  const ticketByCase = useMemo(() => {
    const map = {};
    (customer.tickets || []).forEach((t) => {
      if (t.caseNumber != null) map[String(t.caseNumber)] = t;
    });
    return map;
  }, [customer.tickets]);

  const openTickets = useMemo(
    () => (customer.tickets || []).filter((t) => isNewOpenWip(t)),
    [customer.tickets]
  );

  const selectCaseByNumber = (caseNumber) => {
    if (!caseNumber) return;
    const match = ticketByCase[String(caseNumber)]
      || (customer.tickets || []).find((t) => String(t.caseNumber) === String(caseNumber));
    if (!match) return;
    setSelectedTicket((prev) => (
      prev && (prev.id === match.id || String(prev.caseNumber) === String(match.caseNumber))
        ? null
        : match
    ));
  };

  useEffect(() => {
    if (!selectedTicket || !detailRef.current) return;
    detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedTicket]);

  const monthKeyNow = useMemo(() => {
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  }, []);

  const netMonthCases = useMemo(() => {
    const enrichCaseRow = (row = {}) => {
      const ticket = ticketByCase[String(row.caseNumber)] || {};
      const closedDate = row.closedDate
        || ticket.closedDate
        || (!ticket.isOpen ? ticket.lastModifiedDate : null)
        || null;
      return {
        ...row,
        createdDate: row.createdDate || ticket.createdDate || null,
        closedDate,
        ageDays: row.ageDays ?? ticket.ageDays ?? 0,
        priority: row.priority || ticket.priority || null
      };
    };

    const lv = insights.leadershipView || {};
    let opened;
    let closed;
    const label = lv.netMonthLabel || null;
    if (Array.isArray(lv.netMonthOpenedCases) || Array.isArray(lv.netMonthClosedCases)) {
      opened = (lv.netMonthOpenedCases || []).map(enrichCaseRow);
      closed = (lv.netMonthClosedCases || []).map(enrichCaseRow);
    } else {
      opened = [];
      closed = [];
      (customer.tickets || []).forEach((t) => {
        if (t.createdDate) {
          const dt = new Date(t.createdDate);
          const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
          if (key === monthKeyNow) {
            opened.push(enrichCaseRow({
              caseNumber: t.caseNumber,
              createdDate: t.createdDate,
              closedDate: t.closedDate || null
            }));
          }
        }
        const closedAt = t.closedDate || (!t.isOpen ? t.lastModifiedDate : null);
        if (closedAt) {
          const dt = new Date(closedAt);
          const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
          if (key === monthKeyNow) {
            closed.push(enrichCaseRow({
              caseNumber: t.caseNumber,
              createdDate: t.createdDate || null,
              closedDate: closedAt
            }));
          }
        }
      });
    }
    const netValue = typeof lv.netBacklogLastMonth === 'number'
      ? lv.netBacklogLastMonth
      : opened.length - closed.length;
    return { opened, closed, label, netValue };
  }, [insights.leadershipView, customer.tickets, monthKeyNow, ticketByCase]);

  const queueCases = useMemo(() => {
    const newCases = [];
    const openCases = [];
    const wipCases = [];
    const attentionCases = [];
    openTickets.forEach((t) => {
      const bucket = queueBucket(t.state);
      if (!bucket) return;
      const idleDays = daysSince(t.lastModifiedDate ?? t.lastCustomerActivity);
      const row = {
        caseNumber: t.caseNumber,
        state: bucket,
        priority: t.priority || null,
        ageDays: t.ageDays ?? 0,
        lastModifiedDate: t.lastModifiedDate || t.lastCustomerActivity || null,
        idleDays: idleDays ?? 0
      };
      if (bucket === 'New') newCases.push(row);
      else if (bucket === 'Open') openCases.push(row);
      else wipCases.push(row);
      if (isAttentionIdle(idleDays, t.priority)) attentionCases.push(row);
    });
    const byAgeDesc = (a, b) => (b.ageDays || 0) - (a.ageDays || 0);
    const byIdleDesc = (a, b) => (b.idleDays || 0) - (a.idleDays || 0);
    return {
      newCases: newCases.sort(byAgeDesc),
      openCases: openCases.sort(byAgeDesc),
      wipCases: wipCases.sort(byAgeDesc),
      attentionCases: attentionCases.sort(byIdleDesc)
    };
  }, [openTickets]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ textTransform: 'none', color: DEEP_GREEN, fontWeight: 600, mb: 1.5, px: 0 }}
      >
        Back to Top Customers
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: DEEP_GREEN, wordBreak: 'break-word' }}>
            {customer.name}
          </Typography>
          {agesRefreshing && (
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 0.35 }}>
              Updating L1/L2/L3 ages from ADO…
            </Typography>
          )}
        </Box>
      </Box>

      <CustomerBriefingSection
        accountId={customer.accountId}
        accountIds={customer.accountIds}
        customerName={customer.name}
        syncedAt={customer.syncedAt}
      />

      <Box sx={{ width: '100%', minWidth: 0, mb: 2 }}>
        {OpenCasesTable && Array.isArray(customer.l1Tickets) ? (
          // Same table as the L1 Open tab: same columns, filters and Ticket Journey on row click.
          <OpenCasesTable
            key={customer.id}
            tickets={customer.l1Tickets}
            title="Salesforce · Open cases"
            accent={L1_ACCENT}
            badgeBg={L1_BADGE_BG}
            emptyMessage="No open cases."
            showProductFilter
            showChannelFilter={false}
            sfColumnSet="full"
            initialStatusFilter="all"
            initialRowsPerPage={500}
          />
        ) : (
          <CustomerTicketsTable
            tickets={openTickets}
            selectedTicketId={selectedTicket?.id || null}
            onSelectTicket={setSelectedTicket}
            title="Open cases"
          />
        )}
      </Box>

      {selectedTicket && (
        <Box ref={detailRef} sx={{ mt: 0, mb: 2, scrollMarginTop: 16 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: DEEP_GREEN }}>
              Case details · {selectedTicket.caseNumber}
            </Typography>
            <Button
              size="small"
              onClick={() => setSelectedTicket(null)}
              sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}
            >
              Close
            </Button>
          </Box>
          <TicketFollowUpPanel ticket={selectedTicket} />
        </Box>
      )}

      {/* Insight boards — same minmax fit as Overview / Top Customers */}
      <Box sx={DETAIL_INSIGHT_GRID}>
        <Box sx={{ minWidth: 0, height: '100%' }}>
        <ChartCard
          compact
          title="Volume & open backlog"
          purpose="Opened vs closed · open backlog size"
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.5, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: CHART_CREATED }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: INK }}>Inflow</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: CHART_CLOSED }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: INK }}>Outflow</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: CHART_OPEN_BACKLOG }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: INK }}>Open</Typography>
            </Box>
          </Box>
          <Box sx={{ height: 160, width: '100%', minWidth: 0, flexShrink: 0, overflow: 'hidden' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyData} margin={{ top: 2, right: 2, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,46,34,0.08)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: MUTED }}
                  interval={1}
                  angle={-35}
                  textAnchor="end"
                  height={28}
                />
                <YAxis tick={{ fontSize: 9, fill: MUTED }} allowDecimals={false} width={20} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', fontSize: 11 }} />
                <Bar dataKey="opened" name="Inflow" fill={CHART_CREATED} radius={[3, 3, 0, 0]} maxBarSize={8} />
                <Bar dataKey="closed" name="Outflow" fill={CHART_CLOSED} radius={[3, 3, 0, 0]} maxBarSize={8} />
                <Line
                  type="monotone"
                  dataKey="open"
                  name="Open backlog"
                  stroke={CHART_OPEN_BACKLOG}
                  strokeWidth={2}
                  dot={{ r: 1.5, fill: CHART_OPEN_BACKLOG }}
                  activeDot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
          {volumeTakeaway && (
            <Typography sx={{ color: MUTED, fontSize: '0.68rem', mt: 0.4, lineHeight: 1.3, flexShrink: 0, wordBreak: 'break-word' }}>
              {volumeTakeaway}
            </Typography>
          )}
        </ChartCard>
        </Box>

        <Box sx={{ minWidth: 0, height: '100%' }}>
        <ChartCard
          compact
          title="30 / 60 / 90 days"
          purpose="Inflow · outflow · net"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45, flex: 1, minHeight: 0, minWidth: 0 }}>
            {(periodData.length ? periodData : [
              { period: '30d', inflow: 0, outflow: 0, net: 0 },
              { period: '60d', inflow: 0, outflow: 0, net: 0 },
              { period: '90d', inflow: 0, outflow: 0, net: 0 }
            ]).map((row) => {
              const inflow = row.inflow ?? row.new ?? 0;
              const outflow = row.outflow ?? row.closed ?? 0;
              const net = row.net ?? (inflow - outflow);
              const netTone = net > 0 ? 'bad' : net < 0 ? 'good' : 'neutral';
              return (
              <Box key={row.period} sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: MUTED, mb: 0.3 }}>
                  {row.period}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.4, minWidth: 0 }}>
                  <StatTile label="In" value={inflow} tone="neutral" />
                  <StatTile label="Out" value={outflow} tone="neutral" />
                  <StatTile label="Net" value={net > 0 ? `+${net}` : net} tone={netTone} />
                </Box>
              </Box>
              );
            })}
          </Box>
        </ChartCard>
        </Box>

        <Box sx={{ minWidth: 0, height: '100%' }}>
        <InsightCard
          title="Open queue"
          purpose="New / Open / WIP · Attention"
          subtitle={`Idle: Crit ${ATTENTION_IDLE_BY_PRIORITY.Critical}d · High ${ATTENTION_IDLE_BY_PRIORITY.High}d · Med ${ATTENTION_IDLE_BY_PRIORITY.Medium}d · Low ${ATTENTION_IDLE_BY_PRIORITY.Low}d`}
          tone={queueCases.attentionCases.length ? 'watch' : 'good'}
        >
          <QueueAttentionCard
            newCases={queueCases.newCases}
            openCases={queueCases.openCases}
            wipCases={queueCases.wipCases}
            attentionCases={queueCases.attentionCases}
            lastResponse={insights.lastResponse}
            onSelectCase={selectCaseByNumber}
          />
        </InsightCard>
        </Box>

        <Box sx={{ minWidth: 0, height: '100%' }}>
        <InsightCard
          title="Tier stall"
          purpose="Stuck tiers · move medians"
          subtitle={
            insights.tierTransition?.stall
              ? `Stuck >${insights.tierTransition.stall.thresholds?.L1 || 14}d L1/L2 · >${insights.tierTransition.stall.thresholds?.L3 || 21}d L3`
              : 'Dwell thresholds + recent moves'
          }
          tone={
            ((insights.tierTransition?.stall?.stuckL1?.count || 0)
              + (insights.tierTransition?.stall?.stuckL2?.count || 0)
              + (insights.tierTransition?.stall?.stuckL3?.count || 0)) > 0
              ? 'watch'
              : 'good'
          }
        >
          <StallAnalysisCard
            stall={insights.tierTransition?.stall}
            onSelectCase={selectCaseByNumber}
          />
        </InsightCard>
        </Box>

        <Box sx={{ minWidth: 0, height: '100%' }}>
        <InsightCard
          title="Net month"
          purpose="Opened − closed this month"
          tone={metricTones.netMonthCard}
          centerContent
        >
          <NetMonthCard
            netValue={netMonthCases.netValue}
            label={netMonthCases.label}
            opened={netMonthCases.opened}
            closed={netMonthCases.closed}
            onSelectCase={selectCaseByNumber}
          />
        </InsightCard>
        </Box>

        <Box sx={{ minWidth: 0, height: '100%' }}>
        <InsightCard
          title="Delivery health"
          purpose="Open aging vs SLA"
          tone={insights.deliveryHealth?.oldOpen ? 'bad' : insights.deliveryHealth?.staleOpen ? 'watch' : 'good'}
        >
          <DeliveryHealthCard
            overdueOpen={insights.deliveryHealth?.overdueOpen || 0}
            staleOpen={insights.deliveryHealth?.staleOpen || 0}
            oldOpen={insights.deliveryHealth?.oldOpen || 0}
            oldestOpenDays={insights.deliveryHealth?.oldestOpenDays ?? null}
          />
        </InsightCard>
        </Box>
      </Box>

      {customer.instanceName && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: DEEP_GREEN, mb: 0.75 }}>
            System Health — {customer.instanceName}
          </Typography>
          {collabHealth.loading && (
            <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>Loading production analysis data…</Typography>
          )}
          {!collabHealth.loading && collabHealth.error && (
            <Typography sx={{ fontSize: '0.75rem', color: WARN_ORANGE }}>
              Unable to load system health: {collabHealth.error}
            </Typography>
          )}
          {!collabHealth.loading && !collabHealth.error
            && !collabHealth.heapEntry && !collabHealth.dbEntry && !collabHealth.threadEntry && (
            <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>
              No memory/DB/thread analysis data found for Instance Name &ldquo;{customer.instanceName}&rdquo;.
              Open Release planning → Production analysis to sync it.
            </Typography>
          )}
          {!collabHealth.loading && (collabHealth.heapEntry || collabHealth.dbEntry || collabHealth.threadEntry) && (
            <>
              {collabOverview && (
                <Paper variant="outlined" sx={{ p: 2, mb: 1.25, borderRadius: '10px', borderColor: 'rgba(26,46,34,0.12)' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                      label={collabOverview.overallLevel ? collabOverview.overallLevel.charAt(0).toUpperCase() + collabOverview.overallLevel.slice(1) : 'No Data'}
                      color={collabOverview.overallLevel ? (HEALTH_LEVEL_MUI_COLOR[collabOverview.overallLevel] || 'default') : 'default'}
                      sx={{ fontWeight: 700 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: INK }}>{collabOverview.displayName}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>
                        {collabOverview.region ? `Region: ${collabOverview.region}` : ''}
                        {collabOverview.computedAt ? `${collabOverview.region ? ' · ' : ''}Last analysed: ${new Date(collabOverview.computedAt).toLocaleString()}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25, mb: 1.25 }}>
                <CollabHealthCard label="Memory (JVM Heap)" entry={collabHealth.heapEntry} kind="memory" />
                <CollabHealthCard label="DB Connection" entry={collabHealth.dbEntry} kind="db" />
                <CollabHealthCard label="Tomcat Threads" entry={collabHealth.threadEntry} kind="threads" />
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px', borderColor: 'rgba(26,46,34,0.12)' }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: INK, mb: 1.5 }}>Status Change History (last 30 days)</Typography>
                {collabTransitions.loading && (
                  <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>Loading changes…</Typography>
                )}
                {!collabTransitions.loading && collabTransitions.items.length === 0 && (
                  <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>No status changes recorded in the last 30 days.</Typography>
                )}
                {!collabTransitions.loading && collabTransitions.items.map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.78rem', color: MUTED, minWidth: 84, fontFamily: 'monospace' }}>{t.dayKey}</Typography>
                    <Chip size="small" label={t.analysisLabel} variant="outlined" />
                    <Chip size="small" label={t.previousLevel || 'none'} color={HEALTH_LEVEL_MUI_COLOR[t.previousLevel] || 'default'} />
                    <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>→</Typography>
                    <Chip size="small" label={t.level || 'none'} color={HEALTH_LEVEL_MUI_COLOR[t.level] || 'default'} />
                    {t.direction === 'worsened' && <Typography sx={{ fontSize: '0.78rem', color: '#d32f2f', fontWeight: 700 }}>↑ Worsened</Typography>}
                    {t.direction === 'improved' && <Typography sx={{ fontSize: '0.78rem', color: '#2e7d32', fontWeight: 700 }}>↓ Improved</Typography>}
                    {t.recommendationTitle && <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>— {t.recommendationTitle}</Typography>}
                  </Box>
                ))}
              </Paper>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CustomerDetailView;
